import { createFileRoute } from "@tanstack/react-router";
import { Footer } from "@/components/ui/footer";
import { Navbar } from "@/components/ui/navbar";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://wayra.cc";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicyPage,
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/privacy` }],
  }),
});

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 px-6 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-600 mb-12">
            <strong>Last Updated: February 24, 2026</strong>
          </p>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 mb-8">
              Wayra ("Wayra," "we," "us," or "our") is a product of Wayra Labs
              Inc. Wayra is a cycling and endurance event discovery platform
              that connects athletes with events and registration services
              offered by third-party organizers and registration platforms. This
              Privacy Policy describes how we collect, use, and share
              information when you use our website at wayra.cc and related
              services (collectively, the "Service").
            </p>

            <p className="text-slate-700 mb-12">
              By using the Service, you agree to the collection and use of
              information in accordance with this policy. Please note that your
              use of the Service is additionally subject to our{" "}
              <a href="/terms" className="text-blue-600 hover:underline">
                Terms of Service
              </a>
              .
            </p>

            <hr className="border-slate-200 my-12" />

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                1. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Information You Provide
              </h3>

              <ul className="space-y-4 mb-8">
                <li className="text-slate-700">
                  <strong>Account Information.</strong> When you sign up for a
                  waitlist, create an account, or claim an event, we collect
                  your name, email address, and selected region or location
                  preferences.
                </li>
                <li className="text-slate-700">
                  <strong>Organizer Information.</strong> If you are an event
                  organizer claiming or creating an event listing, we collect
                  information you provide about yourself and your event,
                  including contact details, event descriptions, route files,
                  images, and logistics information.
                </li>
                <li className="text-slate-700">
                  <strong>Payment Information.</strong> If you register for an
                  event through Wayra's native hosting, payment is processed by
                  our third-party payment processor. We do not store your full
                  credit card number, bank account number, or other sensitive
                  financial data on our servers. Our payment processor may
                  collect and store this information in accordance with their
                  own privacy policy.
                </li>
                <li className="text-slate-700">
                  <strong>Communications.</strong> When you contact us directly,
                  we may receive your name, email address, and the contents of
                  your message.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Information Collected Automatically
              </h3>

              <ul className="space-y-4 mb-8">
                <li className="text-slate-700">
                  <strong>Usage Data.</strong> We use Mixpanel to collect
                  analytics data about how you interact with the Service,
                  including pages visited, features used, events viewed, search
                  queries, referral sources, and session duration.
                </li>
                <li className="text-slate-700">
                  <strong>Device Information.</strong> We collect information
                  about the device and browser you use to access the Service,
                  including device type, operating system, browser type, and
                  screen resolution.
                </li>
                <li className="text-slate-700">
                  <strong>Log Data.</strong> Our servers automatically record
                  information when you access the Service, including your IP
                  address, access times, and referring URLs.
                </li>
                <li className="text-slate-700">
                  <strong>Cookies and Similar Technologies.</strong> We use
                  cookies and similar tracking technologies to maintain your
                  session, remember your preferences, and collect analytics
                  data. We categorize our cookies as follows:
                  <ul className="mt-4 ml-6 space-y-3">
                    <li>
                      <strong>Required Cookies.</strong> Necessary for the
                      Service to function, including session management and
                      authentication. These cannot be disabled without affecting
                      core functionality.
                    </li>
                    <li>
                      <strong>Analytical Cookies.</strong> We use Mixpanel, a
                      third-party analytics service, to understand how users
                      interact with the Service. Mixpanel collects information
                      about pages visited, features used, events viewed, search
                      queries, and session duration. Mixpanel may use cookies
                      and similar technologies to collect and store this
                      information. You can learn more about Mixpanel's data
                      practices at{" "}
                      <a
                        href="https://mixpanel.com/legal/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        mixpanel.com/legal/privacy-policy
                      </a>
                      .
                    </li>
                  </ul>
                  <p className="mt-4">
                    You can control cookies through your browser settings,
                    though some features of the Service may not function
                    properly without them. We do not currently use advertising
                    or targeted marketing cookies.
                  </p>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Information from Third-Party Sources
              </h3>

              <ul className="space-y-4">
                <li className="text-slate-700">
                  <strong>Event Data.</strong> We collect and display publicly
                  available factual information about cycling and endurance
                  events, including event names, dates, locations, distances,
                  entry fees, disciplines, and registration URLs. This
                  information is sourced from publicly accessible registration
                  platforms and event calendars. We aggregate this factual data
                  to provide a comprehensive event discovery service. This data
                  is factual in nature and is not obtained from private,
                  authenticated, or restricted sources. We do not reproduce
                  copyrighted content such as event descriptions, images, or
                  logos from third-party sources without authorization.
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                2. How We Use Your Information
              </h2>

              <p className="text-slate-700 mb-4">
                We use the information we collect to:
              </p>

              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Provide, maintain, and improve the Service</li>
                <li>Create and manage your account</li>
                <li>Display event listings and enable event discovery</li>
                <li>Process event registrations and payments</li>
                <li>
                  Communicate with you about the Service, including updates,
                  notifications, and responses to your inquiries
                </li>
                <li>
                  Analyze usage patterns to improve the Service and user
                  experience
                </li>
                <li>
                  Detect, prevent, and address technical issues, fraud, or abuse
                </li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                3. How We Share Your Information
              </h2>

              <p className="text-slate-700 mb-4">
                We do not sell your personal information. We may share your
                information in the following circumstances:
              </p>

              <ul className="space-y-4">
                <li className="text-slate-700">
                  <strong>Service Providers.</strong> We share information with
                  third-party service providers that help us operate the
                  Service, including Mixpanel (analytics), our hosting provider,
                  our email service provider, and our payment processor. These
                  providers are contractually obligated to use your information
                  only as necessary to provide services to us.
                </li>
                <li className="text-slate-700">
                  <strong>Event Organizers.</strong> If you register for an
                  event through the Service, we share your registration
                  information with the event organizer as necessary to
                  facilitate your participation.
                </li>
                <li className="text-slate-700">
                  <strong>Public Event Information.</strong> Event listings,
                  including information provided by organizers who claim events,
                  are displayed publicly on the Service.
                </li>
                <li className="text-slate-700">
                  <strong>Legal Requirements.</strong> We may disclose your
                  information if required to do so by law or in response to
                  valid legal process, such as a subpoena, court order, or
                  government request.
                </li>
                <li className="text-slate-700">
                  <strong>Business Transfers.</strong> If Wayra Labs Inc. is
                  involved in a merger, acquisition, or sale of assets, your
                  information may be transferred as part of that transaction.
                </li>
                <li className="text-slate-700">
                  <strong>With Your Consent.</strong> We may share your
                  information with third parties when you have given us explicit
                  consent to do so.
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                4. Data Retention
              </h2>

              <p className="text-slate-700 mb-4">
                We retain your personal information for as long as your account
                is active or as needed to provide the Service. If you request
                deletion of your account, we will delete your personal
                information within 30 days, except where we are required to
                retain it for legal or legitimate business purposes.
              </p>

              <p className="text-slate-700">
                Aggregated, anonymized data that cannot be used to identify you
                may be retained indefinitely for analytical purposes.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                5. Your Rights and Choices
              </h2>

              <ul className="space-y-4">
                <li className="text-slate-700">
                  <strong>Access and Update.</strong> You may access and update
                  your account information at any time through the Service.
                </li>
                <li className="text-slate-700">
                  <strong>Deletion.</strong> You may request deletion of your
                  account and personal data by contacting us at{" "}
                  <a
                    href="mailto:privacy@wayra.cc"
                    className="text-blue-600 hover:underline"
                  >
                    privacy@wayra.cc
                  </a>
                  .
                </li>
                <li className="text-slate-700">
                  <strong>Email Communications.</strong> You may opt out of
                  promotional emails by clicking the "unsubscribe" link in any
                  email. You may still receive transactional emails related to
                  your account or registrations.
                </li>
                <li className="text-slate-700">
                  <strong>Cookies.</strong> You can control cookies through your
                  browser settings. Note that disabling cookies may affect the
                  functionality of the Service.
                </li>
                <li className="text-slate-700">
                  <strong>California Residents.</strong> If you are a California
                  resident, you may have additional rights under the California
                  Consumer Privacy Act (CCPA), including the right to know what
                  personal information we collect, the right to request
                  deletion, and the right to opt out of the sale of personal
                  information. We do not sell personal information. To exercise
                  your rights, contact us at{" "}
                  <a
                    href="mailto:privacy@wayra.cc"
                    className="text-blue-600 hover:underline"
                  >
                    privacy@wayra.cc
                  </a>
                  .
                </li>
              </ul>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                6. Security
              </h2>

              <p className="text-slate-700">
                We implement reasonable administrative, technical, and physical
                security measures to protect your personal information. However,
                no method of transmission over the Internet or method of
                electronic storage is 100% secure, and we cannot guarantee
                absolute security.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 7 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                7. Children's Privacy
              </h2>

              <p className="text-slate-700">
                The Service is not directed to children under the age of 13. We
                do not knowingly collect personal information from children
                under 13. If we become aware that we have collected personal
                information from a child under 13, we will take steps to delete
                that information promptly. If you believe a child under 13 has
                provided us with personal information, please contact us at{" "}
                <a
                  href="mailto:privacy@wayra.cc"
                  className="text-blue-600 hover:underline"
                >
                  privacy@wayra.cc
                </a>
                .
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 8 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                8. Third-Party Links and Registration Platforms
              </h2>

              <p className="text-slate-700 mb-4">
                The Service may contain links to third-party websites, including
                event registration platforms. When you click through to register
                for an event on a third-party platform, your information is
                provided directly to that platform and is subject to their
                privacy policy, not ours. We do not control and are not
                responsible for the privacy practices of third-party
                registration platforms or event organizers.
              </p>

              <p className="text-slate-700">
                Event organizers who use Wayra's native registration hosting are
                independent data controllers with respect to the participant
                information they receive through the Service. We encourage you
                to review the privacy practices of any event organizer before
                registering for their event.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 9 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                9. Changes to This Policy
              </h2>

              <p className="text-slate-700">
                We may update this Privacy Policy from time to time. We will
                notify you of material changes by posting the updated policy on
                the Service and updating the "Last Updated" date. Your continued
                use of the Service after changes are posted constitutes your
                acceptance of the updated policy.
              </p>
            </section>

            <hr className="border-slate-200 my-12" />

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                10. Contact Us
              </h2>

              <p className="text-slate-700 mb-4">
                If you have questions about this Privacy Policy or our data
                practices, please contact us at:
              </p>

              <div className="text-slate-700">
                <p className="font-semibold">Wayra Labs Inc.</p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:privacy@wayra.cc"
                    className="text-blue-600 hover:underline"
                  >
                    privacy@wayra.cc
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
